'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchImages } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef, useState } from 'react';
const DEFAULT_DEBOUNCE_MS = 500;
interface UseImageSearchPrefetchOptions {
  /** Master gate — when false, the hook is a no-op. Use this to skip
   *  prefetching when the dialog is closed or the user isn't on a step
   *  where the picker will be reachable. */
  enabled: boolean;
  /** Debounce window in ms. Defaults to 500ms — long enough to absorb a
   *  typical typing burst but short enough to feel "anticipated" by the
   *  time the user reaches the picker. */
  debounceMs?: number;
}

/**
 * Background warm-up of the image-search backend response, debounced so we
 * don't hammer the API on every keystroke. Once the debounced value
 * stabilises, fires `useSearchImages.mutateAsync` against the same route the
 * picker hits later, then discards the response. The backend's own response
 * cache + the SDK's network warm-up (TCP keep-alive, DNS, TLS) mean the
 * picker's first fetch lands faster.
 *
 * Unlike the v1 implementation this does NOT populate a TanStack cache: the
 * search picker now uses local-state accumulation (see `<SearchGrid>`), so
 * there is no client-side cache to warm. The benefit is exclusively
 * backend-side and network-warmup — small but free.
 */
export function useImageSearchPrefetch(query, options) {
  const $ = _c(12);
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const searchMutation = useSearchImages();
  const [debounced, setDebounced] = useState(query);
  let t0;
  let t1;
  if ($[0] !== debounceMs || $[1] !== query) {
    t0 = () => {
      const handle = setTimeout(() => {
        setDebounced(query);
      }, debounceMs);
      return () => {
        clearTimeout(handle);
      };
    };
    t1 = [query, debounceMs];
    $[0] = debounceMs;
    $[1] = query;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  const lastFiredRef = useRef(null);
  let t2;
  if ($[4] !== debounced || $[5] !== options.enabled || $[6] !== searchMutation) {
    t2 = () => {
      if (!options.enabled) {
        return;
      }
      const trimmed = debounced.trim();
      if (trimmed.length === 0) {
        return;
      }
      if (lastFiredRef.current === trimmed) {
        return;
      }
      lastFiredRef.current = trimmed;
      searchMutation.mutateAsync({
        query: trimmed
      }).catch(() => {
        if (lastFiredRef.current === trimmed) {
          lastFiredRef.current = null;
        }
      });
    };
    $[4] = debounced;
    $[5] = options.enabled;
    $[6] = searchMutation;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== debounced || $[9] !== options.enabled || $[10] !== searchMutation.mutateAsync) {
    t3 = [debounced, options.enabled, searchMutation.mutateAsync];
    $[8] = debounced;
    $[9] = options.enabled;
    $[10] = searchMutation.mutateAsync;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useEffect(t2, t3);
}